import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ShoppingBagIcon } from 'lucide-react';
import { Apparel } from '../../data/retail';
import { money } from '../../data/food';
import { MerchantBadge } from '../MerchantBadge';
import { merchantFor } from '../../data/merchants';
import { ApparelDetailModal } from './ApparelDetailModal';
import type { ListingCardProps } from '../ListingCard';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../AuthContext';

/**
 * Clothes are bought on fit and colour, so both choices happen on the card —
 * the swatch changes the photo, and the size row shows what is actually in
 * stock. A 4:5 frame gives the garment the room a fashion image needs, and
 * there is no spec grid because material and fit say everything that matters.
 */
export function ApparelCard(props: ListingCardProps & {
  apparel: Apparel;
}) {
  const {
    id,
    title,
    location,
    apparel
  } = props;
  const {
    requireAuth
  } = useAuth();
  const {
    add,
    setOpen,
    lines
  } = useCart();
  const [colourId, setColourId] = useState(apparel.colours[0].id);
  const [size, setSize] = useState<string | null>(null);
  const [details, setDetails] = useState(false);
  const colour = apparel.colours.find(c => c.id === colourId) ?? apparel.colours[0];
  const store = location.split(' · ')[0];
  const inBag = lines.filter(l => l.listingId === id);
  const bagQty = inBag.reduce((n, l) => n + l.qty, 0);
  /** Keeps the row one line at any inventory depth. */
  const MAX_ON_CARD = 5;
  const shown = apparel.sizes.slice(0, MAX_ON_CARD);
  const hidden = apparel.sizes.length - shown.length;
  const addToBag = () => requireAuth(() => {
    if (!size) return;
    add({
      listingId: id,
      merchant: store,
      title,
      image: colour.image,
      summary: `Size ${size} · ${colour.label}`,
      unitPrice: apparel.price,
      qty: 1
    });
    setSize(null);
  });
  return <motion.div whileHover={{
    y: -4
  }} transition={{
    duration: 0.2,
    ease: [0.23, 1, 0.32, 1]
  }} className="transform-gpu bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-soft hover:shadow-medium transition-shadow duration-200 ease-out flex flex-col h-full group" data-id="element-3237" data-mp="2m51gy9pdygwz">
      {/* Square frame: still shows the garment, but a row of these no longer
          pushes everything else below the fold. Colour lives on the image,
          where it is already being judged. */}
      <div className="relative aspect-square overflow-hidden bg-slate-100" data-id="element-3238" data-mp="27hzlzp2hflob">
        <img src={colour.image} alt={`${title} in ${colour.label}`} className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]" data-id="element-3239" data-mp="2bkw0p4owixmz" />
        {apparel.wasPrice && <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-dark-navy text-white text-[10.5px] font-bold" data-id="element-3240" data-mp="h864ggb29ccf">
            {Math.round((1 - apparel.price / apparel.wasPrice) * 100)}% off
          </span>}
        <div className="absolute top-2 right-2" data-id="element-3241" data-mp="eo01oby6nrxg">
          <MerchantBadge merchant={merchantFor(apparel.brand, 'Boutique')} variant="frosted" data-id="element-3242" data-mp="9uf9x1n6nu6p" />
        </div>
        {apparel.colours.length > 1 && <div className="absolute bottom-2 left-2 flex items-center gap-1.5 px-1.5 py-1.5 rounded-full bg-white/90 backdrop-blur-sm shadow-sm" data-id="element-3243" data-mp="3w3g74d424gqo">
            {apparel.colours.map(c => <button key={c.id} onClick={() => setColourId(c.id)} aria-label={c.label} aria-pressed={c.id === colourId} className={`w-[18px] h-[18px] rounded-full border transition-[box-shadow,border-color] duration-150 ease-out ${c.id === colourId ? 'border-dark-navy ring-2 ring-dark-navy/20' : 'border-slate-300 hover:border-slate-500'}`} style={{
          backgroundColor: c.hex
        }} data-id="element-3244" data-mp="slg8hoke5npq" />)}
          </div>}
      </div>

      <div className="p-3 flex flex-col flex-1" data-id="element-3245" data-mp="w0zop74kd0h3">
        {/* Name and price on one line — they are read together */}
        <div className="flex items-start justify-between gap-2" data-id="element-3246" data-mp="m4wldxxz6vsk">
          <h3 className="font-display font-bold text-[14.5px] leading-snug text-dark-navy line-clamp-1 min-w-0" data-id="element-3247" data-mp="m2juuwe5jd3w">
            {title}
          </h3>
          <span className="font-display font-bold text-[15px] text-dark-navy leading-snug flex-shrink-0" data-id="element-3248" data-mp="1et0rgkkglavs">
            {money(apparel.price)}
          </span>
        </div>
        {/* Fabric and the old price — one quiet line, not three */}
        <p className="text-[11px] text-slate-400 mt-0.5 truncate" data-id="element-3249" data-mp="15scfdvemlklj">
          {apparel.material}
          {apparel.wasPrice && <span className="line-through ml-1.5" data-id="element-3250" data-mp="3qiuqugkkqfin">
              {money(apparel.wasPrice)}
            </span>}
        </p>

        {/* Size — the stock reality is on the chip itself. A fixed-width row,
            so a shirt with five sizes and jeans with fourteen match here; the
            rest live in the fitting room, one tap away. */}
        <div className="flex items-center gap-1 mt-2" data-id="element-3251" data-mp="3i1avsoyobvvm">
          {shown.map(s => <button key={s.label} disabled={!s.inStock} onClick={() => setSize(s.label)} title={s.inStock ? undefined : 'Sold out'} className={`w-[34px] flex-shrink-0 px-1 py-1 rounded-md border text-[11.5px] font-bold truncate transition-colors duration-150 ease-out ${!s.inStock ? 'border-slate-100 text-slate-300 line-through cursor-not-allowed' : size === s.label ? 'border-dark-navy bg-dark-navy text-white' : 'border-slate-200 text-slate-700 hover:border-slate-400'}`} data-id="element-3252" data-mp="sntdtue4mrq">
              {s.label}
            </button>)}
          {hidden > 0 && <button onClick={() => requireAuth(() => setDetails(true))} className="px-1.5 py-1 rounded-md text-[11.5px] font-bold text-primary-indigo hover:bg-primary-indigo/5 transition-colors duration-150 ease-out flex-shrink-0" data-id="element-3253" data-mp="368z1piaam7vp">
              +{hidden}
            </button>}
        </div>
        <div className="mt-auto pt-2.5" data-id="element-3254" data-mp="3jzc8vifd1ijz">
          {bagQty > 0 ? <button onClick={() => setOpen(true)} className="w-full text-left mb-1.5 text-[11px] font-bold text-success hover:underline truncate" data-id="element-3255" data-mp="2naecxwvvjel8">
              {bagQty} in your bag · view
            </button> : apparel.sizeNote && <p className="text-[10.5px] text-slate-400 mb-1.5 truncate" data-id="element-3256" data-mp="31afcn90ekfz4">
                {colour.label} · {apparel.sizeNote}
              </p>}
          <div className="flex items-center gap-1.5" data-id="element-3257" data-mp="bmy8m9qfvm5x">
            <button onClick={addToBag} disabled={!size} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-dark-navy text-white text-[12.5px] font-bold rounded-lg hover:bg-slate-800 disabled:bg-slate-100 disabled:text-slate-400 transition-colors duration-150 ease-out" data-id="element-3258" data-mp="gim04vse8rel">
              {size ? <>
                  <ShoppingBagIcon className="w-3.5 h-3.5 flex-shrink-0" data-id="element-3259" data-mp="1mbrxkx7vxv3f" />
                  <span className="truncate" data-id="element-3260" data-mp="1iayc0ahx0sdp">Add {size}</span>
                </> : <>
                  <CheckIcon className="w-3.5 h-3.5 flex-shrink-0" data-id="element-3261" data-mp="1dbvbn3cvrac2" />
                  <span className="truncate" data-id="element-3262" data-mp="18d1wl6hy3b9s">Pick a size</span>
                </>}
            </button>
            <button onClick={() => requireAuth(() => setDetails(true))} className="px-2.5 py-2 text-[12.5px] font-semibold text-slate-500 rounded-lg hover:bg-slate-50 hover:text-dark-navy transition-colors duration-150 ease-out flex-shrink-0" data-id="element-3263" data-mp="13egwd72p0rlc">
              Details
            </button>
          </div>
        </div>
      </div>

      {details && <ApparelDetailModal id={id} title={title} location={location} apparel={apparel} initialColourId={colourId} initialSize={size} onClose={() => setDetails(false)} data-id="element-3264" data-mp="2blfpyedli4hd" />}
    </motion.div>;
}